import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconNames, IconButton, ToastVariants, TextField, Dropdown, SelectTypes } from '@dotcom/crown-ui';
import { useDebouncedCallback } from 'use-debounce';
import { useAnalyticsContext } from '../../context/analytics-context';
import { setIsToasterOpen, setProductQuantity, useAppContext } from '../../context/app-context';
import { useInitializationDataContext } from '../../context/data-context';
import { EnvelopeType } from '../../global-types/envelope';
import { useQueryParams } from '../../hooks';
import useAddressSelection from '../../hooks/use-address-selection';
import { useFeatureFlags } from '../../hooks/use-feature-flags';
import { useProjectSummary } from '../../hooks/use-project-summary';
import { config } from '../../regional-config';
import { updateProductQuantity } from '../../services/customization';
import { useEnvelopeSelection } from '../../views/select-envelope/hooks/use-envelope-selection';
import { SummaryDrawer } from '../card-controls/summary-drawer';
import { getMappedOptions } from '../card-controls/summary-drawer/summary-drawer-utils';
import { QuantityChangeDialog } from '../quantity-change-dialog/quantity-change-dialog';
import styles from './quantity.module.scss';

type UpdateQuantityState = (quantity: string) => void;

export const Quantity = () => {
  const [isProjectSummaryOpen, setIsProjectSummaryOpen] = useState(false);
  const [isQuantityChangeDialogOpen, setIsQuantityChangeDialogOpen] = useState(false);
  const [isQuantityDecreasing, setIsQuantityDecreasing] = useState(false);
  const newQuantity = useRef('');
  const { t } = useTranslation();
  const { trackChangeQuantityProject, trackViewProjectSummary } = useAnalyticsContext();
  const queryParams = useQueryParams();
  const { pathname } = useLocation();
  const isEditor = pathname.includes('/edit/');
  const isPreview = pathname.includes('/preview');
  const shouldBeProjectSummary = isEditor || isPreview;
  const [isSavingQuantity, setIsSavingQuantity] = useState(false);
  const navigate = useNavigate();
  const { selectedEnvelope } = useEnvelopeSelection();
  const { appState, appDispatch } = useAppContext();
  const { count, handleResetAddresses, handleSetMaxCount } = useAddressSelection();
  const { productQuantity } = appState;
  const hasQuantityUpdatedAfterPageLoad = useRef(false);
  const isQuantityLinkEnabled = config?.header?.quantityLink;
  const isQuantityInputEnabled = config?.header?.quantityInput;
  const pageLoaded = useRef(false);
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();

  const {
    register,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      quantity: productQuantity,
    },
    mode: 'onChange',
    delayError: 250,
  });
  const { IS_MULT_ADDRESS_ENABLED, IS_HEADER_QUANTITY_DROPDOWN_ENABLED } = useFeatureFlags();
  const { priceBook } = useProjectSummary();
  const quantityOptions = priceBook ? getMappedOptions(priceBook, '|') : [];

  const isValidQuantity = (quantity: string) => Number(quantity) > 0;
  const onCloseSummary = () => {
    setIsProjectSummaryOpen(false);
  };

  // handles the quantity change and updates the project quantity in the context
  const updateQuantityState: UpdateQuantityState = useCallback(
    (quantity) => {
      setProductQuantity(appDispatch, quantity);
      handleSetMaxCount(Number(quantity));
    },
    [appDispatch, handleSetMaxCount],
  );

  // handles the quantity change and updates the project quantity in the backend
  const updateQuantity: (quantity: string) => Promise<void> = useCallback(
    async (quantity) => {
      setIsQuantityChangeDialogOpen(false);
      setIsSavingQuantity(true);
      let hasError = false;
      try {
        updateQuantityState(quantity);
        queryParams.set('qty', quantity);
        navigate({ pathname, search: queryParams.toString() }, { replace: true });
        await updateProductQuantity(quantity, initializedData?.project_id);
      } catch (e) {
        updateQuantityState(productQuantity || '1');
        queryParams.set('qty', productQuantity || '1');
        navigate({ pathname, search: queryParams.toString() }, { replace: true });
        setValue('quantity', productQuantity || '1');
        setIsToasterOpen(appDispatch, {
          title: t('summaryDrawer.quantityChangeErrorTitle'),
          children: t('summaryDrawer.quantityChangeError'),
          variant: ToastVariants.Error,
        });
        hasError = true;
      }
      setIsSavingQuantity(false);
      if (hasError) {
        throw new Error('Error updating quantity');
      }
    },
    [queryParams, window.history, initializedData?.project_id],
  );

  const handleQuantityChange = async (quantity: string, isDecreasing: boolean, addressCount?: number) => {
    const eventLocation = shouldBeProjectSummary ? 'project summary' : 'address summary';
    let message = '';
    try {
      if (isDecreasing) {
        handleResetAddresses({ envelope: selectedEnvelope });
        await updateQuantity(quantity);
      } else {
        await updateQuantity(quantity);
      }
    } catch (e) {
      message = `${t('summaryDrawer.quantityChangeErrorTitle')}`;
    }
    const selectedAction = isPreview ? 'quantity selected' : 'continue';
    trackChangeQuantityProject(eventLocation, quantity, message, !isDecreasing, selectedAction);
    setIsQuantityChangeDialogOpen(false);
  };

  // handles the action button click when the user clicks on the action button in the quantity change dialog
  const handleDialogActionButtonClick = () => {
    const isDecreasing = Number(newQuantity.current) < Number(productQuantity);
    handleQuantityChange(newQuantity.current, isDecreasing, count);
  };

  // handles the cancel button click when the user clicks on the cancel button in the quantity change dialog
  const handleDialogCancelButtonClick = () => {
    const isDecreasing = Number(newQuantity.current) < Number(productQuantity);
    if (!isDecreasing) {
      // if the quantity is increasing, we want to update the quantity in the input field
      handleQuantityChange(newQuantity.current, isDecreasing);
    } else {
      // if the quantity is decreasing, we want to close the dialog and rollback the quantity in the select field
      setIsQuantityChangeDialogOpen(false);
      setValue('quantity', productQuantity);
    }
  };

  // handles the close button click when the user clicks on the close button in the quantity change dialog
  const handleClose = () => {
    setValue('quantity', productQuantity);
    setIsQuantityChangeDialogOpen(false);
  };

  const onUpdateQuantity = async (quantity: string) => {
    if (productQuantity === quantity) {
      return;
    }
    const isDecreasing = Number(quantity) < Number(productQuantity);
    const isBlankEnvelope = selectedEnvelope === EnvelopeType.Blank;
    if (isBlankEnvelope || isEditor) {
      // todo: update this conditions, we need to add validations when user has selected envelope r+r and is in edit mode
      // (isBlankEnvelope || (isEditor && selectedEnvelope !== EnvelopeType.ReturnRecipient))
      // the increase quantity logic is buggy, so we need to fix it with the new conditions
      let message = '';
      try {
        await updateQuantity(quantity);
      } catch (e) {
        message = `${t('summaryDrawer.quantityChangeErrorTitle')}`;
      }

      const selectedAction = shouldBeProjectSummary ? 'quantity selected' : 'continue';
      trackChangeQuantityProject('project summary', quantity, message, !isDecreasing, selectedAction);
      return;
    }
    setIsQuantityDecreasing(isQuantityDecreasing);
    if (IS_MULT_ADDRESS_ENABLED && isDecreasing) {
      setIsQuantityChangeDialogOpen(true);
    } else {
      handleQuantityChange(quantity, isDecreasing);
    }
    newQuantity.current = quantity;
  };

  // Debounce the update quantity function to avoid multiple API calls in a short time
  // when the user is typing in the input quantity field
  const debouncedUpdate = useDebouncedCallback((value) => {
    onUpdateQuantity(value);
  }, 500);

  useEffect(() => {
    // start listening for changes in product quantity
    // after we've loaded the page and confirmed
    // the state equals the query params
    if (
      productQuantity === queryParams.get('qty') &&
      hasQuantityUpdatedAfterPageLoad.current &&
      isEditor &&
      !pageLoaded.current
    ) {
      trackChangeQuantityProject('project summary', productQuantity);
    } else if (productQuantity === queryParams.get('qty')) {
      hasQuantityUpdatedAfterPageLoad.current = true;
    }
    pageLoaded.current = true;
  }, [productQuantity]);

  useEffect(() => {
    updateQuantityState(queryParams.get('qty') ?? '1');
  }, [queryParams, updateQuantityState]);

  useEffect(() => {
    if (isProjectSummaryOpen) {
      trackViewProjectSummary();
    }
  }, [isProjectSummaryOpen]);

  return (
    <div className={styles['quantity-container']}>
      <QuantityChangeDialog
        isIncrementing={false}
        actionButton={handleDialogActionButtonClick}
        cancelButton={handleDialogCancelButtonClick}
        isOpen={isQuantityChangeDialogOpen}
        onClose={handleClose}
      />
      {isQuantityLinkEnabled && IS_HEADER_QUANTITY_DROPDOWN_ENABLED && (
        <Dropdown
          testId="header-quantity-dropdown"
          className={styles['header-quantity-dropdown']}
          ariaLabel="Quantity"
          label="Quantity"
          hideLabel
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onUpdateQuantity(e.target.value)}
          options={quantityOptions}
          variant={SelectTypes.Default}
          value={productQuantity ?? ''}
          isDisabled={isSavingQuantity || quantityOptions.length === 0}
          register={register('quantity')}
        />
      )}
      {isQuantityLinkEnabled && !IS_HEADER_QUANTITY_DROPDOWN_ENABLED && (
        <>
          <IconButton
            testId="quantity-link"
            onClick={() => setIsProjectSummaryOpen(!isProjectSummaryOpen)}
            label={t('header.quantity', { productQuantity })}
            iconName={isProjectSummaryOpen ? IconNames.ArrowsCaretUpBold : IconNames.ArrowsCaretDownBold}
            iconColor="blue"
            iconSize="12"
            iconPosition="right"
            variant="link"
            className={styles['quantity-link']}
          />
          {isProjectSummaryOpen && (
            <SummaryDrawer
              isOpen={isProjectSummaryOpen}
              onClose={onCloseSummary}
              quantity={productQuantity}
              onUpdateQuantity={onUpdateQuantity}
              isSavingQuantity={isSavingQuantity}
              register={register}
            />
          )}
        </>
      )}
      {isQuantityInputEnabled && (
        <TextField
          className={styles['quantity-input']}
          label="Quantity"
          domId="quantity"
          testId="quantity-input"
          isError={Boolean(errors.quantity)}
          helperText={errors.quantity ? errors.quantity.message : ''}
          type="number"
          onChange={(event) => isValidQuantity(event.target.value) && debouncedUpdate(event.target.value)}
          onKeyDown={(event) => ['e', 'E', '+', '-', '.'].includes(event.key) && event.preventDefault()}
          register={register('quantity', {
            required: { value: true, message: t('header.quantityRequiredError') },
            valueAsNumber: true,
          })}
        />
      )}
    </div>
  );
};
